import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber } from '../../../SleepHub/sleepHubApi';
import { getLocalStorageItem } from '../../../../utils/helper';
import SuppressedPanel from './SuppressedPanel';
import LoadFailedPanel from './LoadFailedPanel';
import BarList from './GuidedPathBars';
import {
  orDash,
  TrendPanel,
  WeekdayPanel,
  SplitPanel,
  StartsPanel,
  CompletionNote,
  GuidedPathFootnote
} from './GuidedPathShell';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_COLORS
} from '../../../../component/common/designSystem';
import {
  PLAN_PATHS,
  BREADTH_RAMP,
  ACTIVITY_RAMP,
  TENURE_RAMP,
  planColor
} from './guidedPathTheme';

/**
 * Company panel → Guided Path → Overview.
 *
 * Three things carry the page:
 *
 *  1. HOW MANY PEOPLE HAVE STARTED A PLAN, de-duplicated across both kinds, so
 *     somebody holding an AI plan and a custom plan is one adoption.
 *  2. WHETHER THEY ACTUALLY DO IT. Starting a plan is the number that flatters
 *     the feature — a plan everybody starts and abandons looks identical to a
 *     working one if you only count sign-ups. Follow-through and active days
 *     sit beside the headcount for that reason.
 *  3. WHICH KIND IS CARRYING IT, with take-up measured against one shared
 *     denominator so the two bars are genuinely comparable.
 *
 * WHAT IS NOT ON THIS PAGE: a completion rate (see CompletionNote for why the
 * obvious field is unusable), and anything anybody wrote.
 */
const GuidedPathOverview = ({ fetcher, populationLabel = 'users', basePath }) => {
  const navigate = useNavigate();
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');
  const slug = userData?.slug;

  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  if (loader) return <Loader />;

  // Checked before suppression: a failed request is not a small cohort.
  if (stats?.error) return <LoadFailedPanel />;

  if (stats?.suppressed) {
    return <SuppressedPanel minimum={stats.minimum} what="Guided Path" />;
  }

  const types = stats?.types || [];
  const reported = types.filter((row) => !row.suppressed);
  const withheld = types.filter((row) => row.suppressed);
  const root = basePath || `/${slug}/guided-path`;

  /**
   * Take-up per kind. Both bars share the SAME denominator — the whole
   * population — so they are directly comparable, which two separate donuts
   * could never be. The share is the backend's, not recomputed here.
   */
  const takeUpRows = reported.map((row) => ({
    label: row.label,
    count: row.people,
    share: row.takeUp,
    key: row.key
  }));

  return (
    <div>
      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People With A Plan"
          value={formatBigNumber(stats?.people || 0)}
        />
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title={`Share of ${populationLabel}`}
          value={stats?.totalUsers > 0 ? `${stats.takeUp}%` : '-'}
        />
        {/* Signed up, and still turning up. Either one alone can be made to
            look good; the pair cannot. */}
        <StatCard
          icon={STAT_ICONS.streak}
          title="Plans Still Running"
          value={formatBigNumber(stats?.running || 0)}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Follow-through"
          value={stats?.ritualsOffered > 0 ? `${stats.followThrough}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Rituals Ticked (30d)"
          value={formatBigNumber(stats?.ritualsTicked || 0)}
        />
      </div>

      <CompletionNote />

      {/* A partial total is labelled as one. Silently summing part of a section
          and calling it the total is how a chart starts lying quietly. */}
      {!stats?.totalsAreComplete ? (
        <p className="mt-3 text-xs text-shoorah-secondary dark:text-gray-400">
          One kind of plan is withheld because too few people are using it, so it is left out of the
          comparison below rather than folded in — including it would let the withheld number be
          worked out by subtraction. The headline count of people with a plan covers both kinds and
          is complete.
        </p>
      ) : null}

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ---------- Take-up ---------- */}
        <Panel
          title="Take-up by kind of plan"
          subtitle={`Share of all ${populationLabel}, so the bars compare directly`}
        >
          {takeUpRows.length ? (
            <BarList
              rows={takeUpRows}
              unit="people"
              colorFor={(index, label, row) => planColor(row.key)}
            />
          ) : (
            <PanelEmpty>Neither kind of plan has enough people to report on its own.</PanelEmpty>
          )}
        </Panel>

        {/* ---------- Breadth ---------- */}
        <Panel title="Which kinds people use" subtitle="One kind, the other, or both">
          {stats?.breadthSplit?.length ? (
            <BarList
              rows={stats.breadthSplit}
              unit="people"
              colorFor={(index) => BREADTH_RAMP[index] || CHART_COLORS.gray}
            />
          ) : (
            <PanelEmpty>
              {withheld.length
                ? 'Withheld while one kind of plan is too small to report — with only two kinds, the spread plus a partial total would give the hidden one away.'
                : 'Nobody has started a plan of either kind yet.'}
            </PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Doing the work ---------- */}
      <div className="mt-4">
        <TrendPanel trend={stats?.dailyTrend} />
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StartsPanel trend={stats?.startedTrend} />
        <WeekdayPanel rows={stats?.weekday} />
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SplitPanel
          title="Days active per person"
          subtitle="Distinct days somebody ticked at least one ritual"
          rows={stats?.activeDaySplit}
          ramp={ACTIVITY_RAMP}
          unit="people"
          empty="Nobody has ticked a ritual yet, so there are no active days to spread."
        />
        <SplitPanel
          title="How long people have had a plan"
          subtitle="Measured from the day the plan was created"
          rows={stats?.tenureSplit}
          ramp={TENURE_RAMP}
          unit="plans"
          empty="No plan carries a usable start date."
        />
      </div>

      {/* ---------- Comparison ---------- */}
      <div className="mt-4">
        <Panel
          title="The two kinds side by side"
          subtitle="Take-up, plans running, and whether the rituals get done"
        >
          {reported.length ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Kind of plan</th>
                  <th className="py-2 px-4 font-medium text-center">People</th>
                  <th className="py-2 px-4 font-medium text-center">Take-up</th>
                  <th className="py-2 px-4 font-medium text-center">Plans</th>
                  <th className="py-2 px-4 font-medium text-center">Running</th>
                  <th className="py-2 px-4 font-medium text-center">Active days</th>
                  <th className="py-2 font-medium text-right">Follow-through</th>
                </>
              }
            >
              {reported.map((row) => (
                <tr
                  key={row.key}
                  className={`${PANEL_ROW_CLASS} cursor-pointer`}
                  onClick={() => navigate(`${root}/${PLAN_PATHS[row.key] || row.key}`)}
                >
                  <td className="py-2 pr-4">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: planColor(row.key) }}
                      />
                      {row.label}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.people)}</td>
                  <td className="py-2 px-4 text-center">{row.takeUp}%</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.plans)}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.running)}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.daysEngaged)}</td>
                  <td className="py-2 text-right">{orDash(row.followThrough, '%')}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>
              Neither kind of plan has enough people to report on its own, so there is nothing to
              compare. The headline figures above still cover everybody.
            </PanelEmpty>
          )}

          {withheld.length ? (
            <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
              Withheld here for being too small a group:{' '}
              {withheld.map((row) => row.label).join(', ')}. No count is given, because a count is
              what the threshold exists to hide.
            </p>
          ) : null}
        </Panel>
      </div>

      <GuidedPathFootnote minimum={stats?.minimum} />
    </div>
  );
};

GuidedPathOverview.propTypes = {
  fetcher: PropTypes.func.isRequired,
  populationLabel: PropTypes.string,
  basePath: PropTypes.string
};

export default GuidedPathOverview;
